import { FC, useEffect, useState } from 'react';
import { TextField, Autocomplete, CircularProgress } from '@mui/material';
import { ISelectOption } from '../formik-mui';

export interface AutoCompleteMultipleProps {
  name?: string;
  apiRequest?: () => Promise<any>;
  transformResponse?: (response: any) => ISelectOption[];
  handleOptions?: (data: ISelectOption[]) => void;
  options?: { value: any; label: any }[];
  refetch?: any;
  shouldFetch?: boolean;
  label?: string;
  value: ISelectOption | ISelectOption[] | null;
  required?: boolean;
  disableCloseOnSelect?: boolean;
  onChange?: (event: object, value: ISelectOption | ISelectOption[] | null) => void;
  limitTags?: number;
  disabled?: boolean;
  inputPlaceholder?: string;
}

export const AutoCompleteMultiple: FC<AutoCompleteMultipleProps> = ({
  apiRequest,
  transformResponse,
  handleOptions,
  refetch,
  shouldFetch,
  label,
  value,
  required,
  disableCloseOnSelect,
  onChange,
  limitTags = 2,
  disabled = false,
  inputPlaceholder,
  ...props
}) => {
  const [options, setOptions] = useState<ISelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (shouldFetch && apiRequest) {
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const response = await apiRequest();
          const data = transformResponse(response);
          setOptions(data);
          handleOptions && handleOptions(data);
        } catch (err) {
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [apiRequest, transformResponse, handleOptions, refetch, shouldFetch]);

  const normalizedValue = Array.isArray(value) ? value : value ? [value] : [];

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      disableCloseOnSelect
      size="small"
      limitTags={limitTags}
      options={options}
      getOptionLabel={option => option.label}
      value={normalizedValue}
      filterSelectedOptions
      onChange={(event, newValue) => onChange(event, newValue as ISelectOption[])}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          required={required}
          placeholder={inputPlaceholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  params.InputProps.endAdornment
                )}
              </>
            )
          }}
        />
      )}
      {...props}
    />
  );
};
